import { YStack, styled } from 'tamagui'

const Container = styled(YStack, {
  name: 'NetworkFlowContainer',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
  zIndex: 0,
})

const CYCLE = 10

const NODES: Array<{ x: number; y: number; ring: number; blocked: boolean }> = [
  // Center hub
  { x: 700, y: 350, ring: 0, blocked: false },
  // Ring 1 — inner hexagon (r~100)
  { x: 640, y: 270, ring: 1, blocked: false },
  { x: 760, y: 270, ring: 1, blocked: true },
  { x: 810, y: 350, ring: 1, blocked: false },
  { x: 760, y: 430, ring: 1, blocked: false },
  { x: 640, y: 430, ring: 1, blocked: true },
  { x: 590, y: 350, ring: 1, blocked: false },
  // Ring 2 — mid octagon (r~220)
  { x: 540, y: 170, ring: 2, blocked: false },
  { x: 700, y: 130, ring: 2, blocked: true },
  { x: 860, y: 170, ring: 2, blocked: false },
  { x: 940, y: 350, ring: 2, blocked: false },
  { x: 860, y: 530, ring: 2, blocked: true },
  { x: 700, y: 570, ring: 2, blocked: false },
  { x: 540, y: 530, ring: 2, blocked: false },
  { x: 460, y: 350, ring: 2, blocked: true },
  // Ring 3 — outer edge (r~420, fills viewport)
  { x: 320, y: 90, ring: 3, blocked: false },
  { x: 540, y: 50, ring: 3, blocked: true },
  { x: 860, y: 50, ring: 3, blocked: false },
  { x: 1080, y: 90, ring: 3, blocked: false },
  { x: 1180, y: 230, ring: 3, blocked: true },
  { x: 1200, y: 430, ring: 3, blocked: false },
  { x: 1080, y: 610, ring: 3, blocked: false },
  { x: 860, y: 650, ring: 3, blocked: true },
  { x: 540, y: 650, ring: 3, blocked: false },
  { x: 320, y: 610, ring: 3, blocked: false },
  { x: 200, y: 430, ring: 3, blocked: true },
  { x: 220, y: 230, ring: 3, blocked: false },
]

const EDGES: Array<[number, number]> = [
  // Center → ring 1
  [0, 1], [0, 2], [0, 3], [0, 4], [0, 5], [0, 6],
  // Ring 1 internal
  [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 1],
  // Ring 1 → ring 2
  [1, 7], [1, 8], [2, 8], [2, 9], [3, 9], [3, 10],
  [4, 10], [4, 11], [5, 11], [5, 12], [6, 13], [6, 14],
  // Ring 2 internal
  [7, 8], [8, 9], [9, 10], [10, 11], [11, 12], [12, 13], [13, 14], [14, 7],
  // Ring 2 → ring 3
  [7, 15], [7, 26], [8, 16], [9, 17], [9, 18],
  [10, 19], [10, 20], [11, 21], [12, 22], [12, 23],
  [13, 24], [14, 25], [14, 26],
  // Ring 3 internal
  [15, 16], [16, 17], [17, 18], [18, 19], [19, 20], [20, 21],
  [21, 22], [22, 23], [23, 24], [24, 25], [25, 26], [26, 15],
]

const CENTER = { x: 700, y: 350 }
const MAX_DIST = 520

function dist(a: { x: number; y: number }, b: { x: number; y: number }) {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2)
}

export function NetworkFlowVisualizer() {
  return (
    <Container>
      <svg
        viewBox="0 0 1400 700"
        preserveAspectRatio="xMidYMid slice"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.45,
        }}
      >
        <defs>
          <filter id="nGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="nGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FF6300" />
            <stop offset="100%" stopColor="#FF9D3A" />
          </linearGradient>
        </defs>

        {/* Connection edges */}
        {EDGES.map(([a, b], i) => {
          const n1 = NODES[a]
          const n2 = NODES[b]
          const md = (dist(n1, CENTER) + dist(n2, CENTER)) / 2
          const delay = (md / MAX_DIST) * 3.5
          const isBlocked = n1.blocked || n2.blocked

          return (
            <g key={`e-${i}`}>
              <line
                x1={n1.x} y1={n1.y} x2={n2.x} y2={n2.y}
                stroke={isBlocked ? '#EF4444' : '#FF6300'}
                strokeWidth={1.5}
                className={isBlocked ? 'nf-edge-blocked' : 'nf-edge-open'}
                style={{ animationDelay: `${delay}s` }}
              />
              {!isBlocked && (
                <line
                  x1={n1.x} y1={n1.y} x2={n2.x} y2={n2.y}
                  stroke="#FF9D3A"
                  strokeWidth={2}
                  strokeDasharray="2 14"
                  strokeLinecap="round"
                  className="nf-flow"
                  style={{ animationDelay: `${delay + 1.5}s` }}
                />
              )}
            </g>
          )
        })}

        {/* Nodes */}
        {NODES.map((node, i) => {
          const nd = dist(node, CENTER)
          const delay = (nd / MAX_DIST) * 3.5

          return (
            <g key={`n-${i}`}>
              {node.blocked && (
                <>
                  <circle
                    cx={node.x} cy={node.y} r={12}
                    fill="none" stroke="#EF4444" strokeWidth={1.5}
                    className="nf-block-ring"
                    style={{ animationDelay: `${delay}s` }}
                  />
                  <line
                    x1={node.x - 4} y1={node.y - 4}
                    x2={node.x + 4} y2={node.y + 4}
                    stroke="#EF4444" strokeWidth={2} strokeLinecap="round"
                    className="nf-block-x"
                    style={{ animationDelay: `${delay}s` }}
                  />
                  <line
                    x1={node.x + 4} y1={node.y - 4}
                    x2={node.x - 4} y2={node.y + 4}
                    stroke="#EF4444" strokeWidth={2} strokeLinecap="round"
                    className="nf-block-x"
                    style={{ animationDelay: `${delay}s` }}
                  />
                </>
              )}
              <circle
                cx={node.x} cy={node.y}
                r={node.ring === 0 ? 6 : 4}
                fill={node.blocked ? '#666' : '#FF6300'}
                filter={node.ring === 0 ? 'url(#nGlow)' : undefined}
                className={node.blocked ? 'nf-node-blocked' : 'nf-node-open'}
                style={{ animationDelay: `${delay}s` }}
              />
            </g>
          )
        })}

        {/* Expanding ripple pulses */}
        {[0, 1, 2].map((i) => (
          <circle
            key={`r-${i}`}
            cx={CENTER.x} cy={CENTER.y}
            fill="none"
            stroke="url(#nGrad)"
            strokeWidth={1.5}
            opacity={0}
          >
            <animate
              attributeName="r"
              from="0" to="700"
              dur={`${CYCLE}s`}
              begin={`${i * (CYCLE / 3)}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              from="0.35" to="0"
              dur={`${CYCLE}s`}
              begin={`${i * (CYCLE / 3)}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}
      </svg>

      <style>{`
        .nf-edge-open {
          animation: nfEdgeOn ${CYCLE}s ease-in-out infinite;
          stroke-opacity: 0.06;
        }
        .nf-edge-blocked {
          animation: nfEdgeUnblock ${CYCLE}s ease-in-out infinite;
          stroke-opacity: 0.12;
        }
        .nf-flow {
          animation: nfFlowMove ${CYCLE}s linear infinite;
          stroke-opacity: 0;
        }
        .nf-node-open {
          animation: nfNodeOn ${CYCLE}s ease-in-out infinite;
          opacity: 0.2;
        }
        .nf-node-blocked {
          animation: nfNodeUnblock ${CYCLE}s ease-in-out infinite;
          opacity: 0.15;
        }
        .nf-block-ring {
          animation: nfRingClear ${CYCLE}s ease-in-out infinite;
        }
        .nf-block-x {
          animation: nfXClear ${CYCLE}s ease-in-out infinite;
        }

        @keyframes nfEdgeOn {
          0%, 8% { stroke-opacity: 0.06; stroke: #333; }
          22%, 55% { stroke-opacity: 0.45; stroke: #FF6300; }
          80%, 100% { stroke-opacity: 0.06; stroke: #333; }
        }
        @keyframes nfEdgeUnblock {
          0%, 8% { stroke-opacity: 0.12; stroke: #EF4444; }
          18% { stroke-opacity: 0.9; stroke: #FF6300; }
          28%, 55% { stroke-opacity: 0.45; stroke: #60A5FA; }
          80%, 100% { stroke-opacity: 0.12; stroke: #EF4444; }
        }
        @keyframes nfFlowMove {
          0%, 18% { stroke-opacity: 0; stroke-dashoffset: 16; }
          28%, 65% { stroke-opacity: 0.6; }
          75% { stroke-dashoffset: -80; }
          80%, 100% { stroke-opacity: 0; }
        }
        @keyframes nfNodeOn {
          0%, 8% { opacity: 0.15; }
          22% { opacity: 1; }
          55% { opacity: 0.6; }
          80%, 100% { opacity: 0.15; }
        }
        @keyframes nfNodeUnblock {
          0%, 8% { opacity: 0.12; fill: #666; }
          18% { opacity: 1; fill: #FF6300; }
          28%, 55% { opacity: 0.7; fill: #60A5FA; }
          80%, 100% { opacity: 0.12; fill: #666; }
        }
        @keyframes nfRingClear {
          0%, 8% { stroke: #EF4444; opacity: 0.5; }
          18% { stroke: #FF6300; opacity: 1; }
          28% { stroke: #60A5FA; opacity: 0.4; }
          55%, 100% { stroke: #60A5FA; opacity: 0; }
        }
        @keyframes nfXClear {
          0%, 8% { stroke: #EF4444; opacity: 0.6; }
          18% { stroke: #FF6300; opacity: 0.8; }
          28%, 100% { opacity: 0; }
        }
      `}</style>
    </Container>
  )
}
